import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CreditCard, Landmark, Archive, CheckCircle2, Clock, Download, Target } from "lucide-react";
import { financeApi, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageContainer, SectionTitle, EmptyState, Money, SESSION_LABEL } from "@/components/shared";
import { ProgressRing, ProgressBar, StaggerGroup, StaggerItem, SkeletonGrid } from "@/components/motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function downloadReceipt({ member, collecte, tx }) {
  const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Reçu ASL</title>
  <style>body{font-family:Arial,sans-serif;color:#221f4b;max-width:640px;margin:40px auto;padding:0 20px}
  .h{background:#221f4b;color:#fff;padding:20px;border-radius:12px}.g{color:#3faa5a}.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee}
  .tot{font-weight:bold;font-size:18px;border-top:2px solid #221f4b;margin-top:8px;padding-top:8px}</style></head>
  <body><div class="h"><h2 style="margin:0">ASL — Association Sports et Loisirs</h2><div>Pierre-De-Saurel · Reçu de paiement</div></div>
  <p><b>Membre :</b> ${member}</p><p><b>Objet :</b> ${collecte}</p><p><b>Méthode :</b> ${tx.method === "card" ? "Carte bancaire" : "Virement Interac"}</p>
  <p><b>Date :</b> ${new Date(tx.created_at).toLocaleString("fr-CA")}</p><p><b>Référence :</b> ${tx.stripe_payment_intent_id || tx.interac_reference || tx.session_id}</p>
  <div class="row"><span>Montant</span><span>${Number(tx.base_amount).toFixed(2)} $</span></div>
  <div class="row"><span>Frais de traitement</span><span>${Number(tx.fee || 0).toFixed(2)} $</span></div>
  <div class="row tot"><span>Total payé</span><span class="g">${Number(tx.amount).toFixed(2)} $ CAD</span></div>
  <p style="margin-top:24px;color:#777;font-size:12px">Merci de votre contribution à la communauté ASL.</p></body></html>`;
  const blob = new Blob([html], { type: "text/html" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `recu-asl-${tx.id.slice(0, 8)}.html`; a.click();
}

export default function CollectesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState([]);
  const [archive, setArchive] = useState([]);
  const [myPays, setMyPays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interacOpen, setInteracOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, ar] = await Promise.all([financeApi.collectes(true), financeApi.archive()]);
      setActive(a.data); setArchive(ar.data);
      if (user) { const mp = await financeApi.myPayments(); setMyPays(mp.data); }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user]);

  const paidTxByCollecte = {}; myPays.forEach((p) => { if (p.payment_status === "paid" && p.purpose === "collecte") paidTxByCollecte[p.collecte_id] = p; });
  const pendingInteracIds = new Set(myPays.filter((p) => p.method === "interac" && p.payment_status === "pending").map((p) => p.collecte_id));

  const payCard = async (c) => {
    if (!user) { navigate("/connexion"); return; }
    setBusy(true);
    try { const { data } = await financeApi.checkout({ purpose: "collecte", collecte_id: c.id, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const openInterac = (c) => { if (!user) { navigate("/connexion"); return; } setSelected(c); setReference(""); setInteracOpen(true); };
  const submitInterac = async () => {
    if (!reference.trim()) { toast.error("Référence requise"); return; }
    setBusy(true);
    try { await financeApi.interac({ purpose: "collecte", collecte_id: selected.id, reference }); toast.success("Référence soumise. En attente de validation."); setInteracOpen(false); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <PageContainer>
      <SectionTitle>Cotisations</SectionTitle>
      <p className="-mt-3 text-sm text-muted-foreground">Payez votre cotisation de session pour devenir Membre actif. Les frais de traitement bancaire s'appliquent aux paiements par carte.</p>

      <Tabs defaultValue="active">
        <TabsList><TabsTrigger value="active" data-testid="tab-collectes-actives">En cours</TabsTrigger><TabsTrigger value="archive" data-testid="tab-collectes-archive"><Archive className="mr-1.5 h-4 w-4" />Archives</TabsTrigger></TabsList>

        <TabsContent value="active" className="mt-4">
          {loading ? <SkeletonGrid count={2} cols="sm:grid-cols-2" /> : active.length === 0 ? <EmptyState title="Aucune cotisation active" icon={CreditCard} /> : (
            <StaggerGroup data-testid="cotisations-liste" className="grid gap-5 sm:grid-cols-2">
              {active.map((c) => {
                const tx = paidTxByCollecte[c.id]; const pending = pendingInteracIds.has(c.id);
                return (
                  <StaggerItem key={c.id}>
                    <Card className="card-hover overflow-hidden"><CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div><div className="font-heading font-semibold leading-tight">{c.title}</div><div className="mt-0.5 text-xs text-muted-foreground">Session {SESSION_LABEL[c.session] || c.session} · {c.sport_name}</div></div>
                        <Badge variant="outline">{SESSION_LABEL[c.session] || c.session}</Badge>
                      </div>

                      <div className="mt-4 flex items-center gap-4">
                        {c.goal ? (
                          <ProgressRing value={c.total_collected} max={c.goal} size={92}>
                            <span className="text-sm font-heading font-bold">{Math.round((c.total_collected / c.goal) * 100)}%</span>
                            <span className="text-[9px] text-muted-foreground">objectif</span>
                          </ProgressRing>
                        ) : (
                          <div className="flex h-[92px] w-[92px] flex-col items-center justify-center rounded-full bg-secondary/10"><Money value={c.amount} className="text-lg text-secondary" /></div>
                        )}
                        <div className="flex-1 space-y-1.5 text-sm">
                          <div className="flex justify-between"><span className="text-muted-foreground">Par membre</span><Money value={c.amount} /></div>
                          {c.goal && <><div className="flex justify-between"><span className="text-muted-foreground inline-flex items-center gap-1"><Target className="h-3.5 w-3.5" />Objectif</span><Money value={c.goal} /></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Collecté</span><Money value={c.total_collected} className="text-secondary" /></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Restant</span><Money value={Math.max(0, c.remaining || 0)} /></div></>}
                          <div className="flex justify-between text-xs text-muted-foreground"><span>{c.paid_count} payé(s)</span><span>Éch. {c.deadline?.slice(0, 10)}</span></div>
                        </div>
                      </div>

                      {tx ? (
                        <div className="mt-4 flex items-center gap-2">
                          <div className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-50 py-2 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Réglée</div>
                          <Button variant="outline" size="sm" onClick={() => downloadReceipt({ member: `${user.first_name} ${user.last_name}`, collecte: c.title, tx })} data-testid="cotisation-recu-bouton"><Download className="mr-1.5 h-4 w-4" />Reçu</Button>
                        </div>
                      ) : pending ? (
                        <div className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-50 py-2 text-sm font-medium text-amber-700"><Clock className="h-4 w-4" /> Interac en attente de validation</div>
                      ) : (
                        <div className="mt-4 grid grid-cols-2 gap-2">
                          <Button onClick={() => payCard(c)} disabled={busy} className="bg-secondary hover:bg-emerald-600 text-white" data-testid="cotisation-payer-carte-bouton"><CreditCard className="mr-1.5 h-4 w-4" />Carte</Button>
                          <Button variant="outline" onClick={() => openInterac(c)} data-testid="cotisation-payer-interac-bouton"><Landmark className="mr-1.5 h-4 w-4" />Interac</Button>
                        </div>
                      )}
                    </CardContent></Card>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          )}
        </TabsContent>

        <TabsContent value="archive" className="mt-4">
          {archive.length === 0 ? <EmptyState title="Aucune archive" icon={Archive} /> : (
            <Card><CardContent className="p-0 overflow-x-auto"><Table>
              <TableHeader><TableRow><TableHead>Titre</TableHead><TableHead>Session</TableHead><TableHead>Montant</TableHead><TableHead>Paiements</TableHead><TableHead>Collecté</TableHead></TableRow></TableHeader>
              <TableBody>{archive.map((c) => (<TableRow key={c.id}><TableCell className="font-medium">{c.title}</TableCell><TableCell>{SESSION_LABEL[c.session] || c.session}</TableCell><TableCell><Money value={c.amount} /></TableCell><TableCell>{c.paid_count}</TableCell><TableCell><Money value={c.total_collected} /></TableCell></TableRow>))}</TableBody>
            </Table></CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={interacOpen} onOpenChange={setInteracOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Paiement par virement Interac</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">Virement de <b>{selected ? Number(selected.amount).toFixed(2) : ""} $ CAD</b> à :<br />Courriel : <b>informations.slps@gmail.com</b><br />Mot de passe : <b>adhesion</b></div>
            <div className="space-y-1.5"><Label>Référence de virement *</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="cotisation-interac-reference-input" /></div>
          </div>
          <DialogFooter><Button onClick={submitInterac} disabled={busy} data-testid="cotisation-interac-soumettre">{busy ? "…" : "Soumettre"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
